import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR, INGREDIENT_NOTES, PRODUCTS } from "@/data/products";
import { resolveImage } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

// Nutrition rows come from the database as objects; older static data used [label, value, indent] tuples.
const toRow = (r) => (Array.isArray(r) ? { label: r[0], value: r[1], indent: r[2] } : r);
const toActive = (a) => (Array.isArray(a) ? { name: a[0], dose: a[1] } : a);

const ProductSpotlight = ({ product, index }) => {
  const { add } = useCart();
  const [openIng, setOpenIng] = useState(null);
  const [showNutrition, setShowNutrition] = useState(false);
  const gallery = product.images?.length ? product.images : [product.image].filter(Boolean);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const flipped = index % 2 === 1;
  // Fall back to the built-in formula when the database copy has no ingredients.
  const fallback = PRODUCTS.find((p) => p.id === product.slug);
  const ingredients = product.ingredients?.length ? product.ingredients : fallback?.ingredients || [];
  const formulaName = fallback?.formulaName || "Triple Threat Formula";

  const goTo = (i) => {
    const track = trackRef.current;
    if (track) track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
  };

  const onTrackScroll = (e) => {
    const { scrollLeft, clientWidth } = e.currentTarget;
    if (clientWidth) setActive(Math.round(scrollLeft / clientWidth));
  };

  // Auto-advance; restarts after every slide change so a manual swipe gets a full interval.
  useEffect(() => {
    if (gallery.length < 2 || paused) return undefined;
    const t = setTimeout(() => {
      const track = trackRef.current;
      if (track) track.scrollTo({ left: ((active + 1) % gallery.length) * track.clientWidth, behavior: "smooth" });
    }, 3500);
    return () => clearTimeout(t);
  }, [active, paused, gallery.length]);

  return (
    <article
      className="relative min-h-[85vh] flex items-center py-32 md:py-40 border-b border-[#EBE8E3] overflow-hidden"
      data-testid={`spotlight-${product.slug}`}
    >
      <span
        aria-hidden
        className="absolute top-10 font-serif-lux font-light text-[26vw] leading-none text-transparent select-none pointer-events-none"
        style={{ WebkitTextStroke: "1px #EBE8E3", [flipped ? "right" : "left"]: "-2vw" }}
      >
        {product.chapter}
      </span>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 w-full grid md:grid-cols-12 gap-12 md:gap-24 items-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className={`md:col-span-7 group ${flipped ? "md:order-2" : ""}`}
        >
          {/* Fixed 1:1 frame so every upload displays the same, whatever its size or ratio.
              Swipeable on touch, auto-advances, snaps one image at a time. */}
          <div
            ref={trackRef}
            onScroll={onTrackScroll}
            onPointerDown={() => setPaused(true)}
            onPointerUp={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="flex aspect-square overflow-x-auto snap-x snap-mandatory no-scrollbar bg-white shadow-[0_50px_100px_-30px_rgba(17,17,17,0.4)]"
          >
            {gallery.map((url, i) => (
              <img
                key={url}
                src={resolveImage(url)}
                alt={`${product.name} — ${product.flavor}${gallery.length > 1 ? ` (${i + 1}/${gallery.length})` : ""}`}
                data-testid={i === 0 ? `spotlight-img-${product.slug}` : undefined}
                draggable={false}
                className="w-full h-full shrink-0 snap-center object-cover"
              />
            ))}
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex justify-center items-center gap-1.5 md:hidden" data-testid={`spotlight-dots-${product.slug}`}>
              {gallery.map((url, i) => (
                <button
                  key={url}
                  onClick={() => goTo(i)}
                  aria-label={`Show image ${i + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    i === active ? "w-2 h-2 bg-[#C07C5F]" : "w-1.5 h-1.5 bg-[#CFCAC3]"
                  }`}
                />
              ))}
            </div>
          )}
          {gallery.length > 1 && (
            <div className="mt-6 hidden md:flex gap-3 overflow-x-auto" data-testid={`spotlight-thumbs-${product.slug}`}>
              {gallery.map((url, i) => (
                <button
                  key={url}
                  onClick={() => goTo(i)}
                  aria-label={`Show image ${i + 1}`}
                  className={`shrink-0 w-16 md:w-20 border transition-colors duration-300 ${
                    i === active ?"border-[#C07C5F]" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={resolveImage(url)} alt="" className="w-full aspect-square object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, delay: 0.15, ease: EASE }}
          className={`md:col-span-5 ${flipped ? "md:order-1" : ""}`}
        >
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#C07C5F] mb-6">
            Chapter {product.chapter} — {product.flavor}
          </p>
          <h2 className="font-serif-lux font-medium text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-4" data-testid={`spotlight-name-${product.slug}`}>
            {product.name.replace("DNA ", "")}
            <span className="italic font-light block text-[#5C5C5C] text-3xl md:text-4xl mt-3">
              {product.tagline}
            </span>
          </h2>
          <p className="text-sm md:text-base font-light text-[#5C5C5C] leading-relaxed max-w-sm mt-8">
            {product.description}
          </p>

          {ingredients.length > 0 && (
            <p className="mt-12 text-sm md:text-base tracking-[0.3em] uppercase text-[#C07C5F]" data-testid={`triple-threat-heading-${product.slug}`}>
              {formulaName}
            </p>
          )}
          <ul className="mt-4 border-t border-[#EBE8E3]">
            {ingredients.map((ing) => {
              const isOpen = openIng === ing.name;
              const slug = ing.name.toLowerCase().replace(/[^a-z]/g, "");
              return (
                <li key={ing.name} className="border-b border-[#EBE8E3]">
                  <button
                    onClick={() => setOpenIng(isOpen ? null : ing.name)}
                    data-testid={`ingredient-toggle-${product.slug}-${slug}`}
                    className="w-full flex items-center justify-between py-4 text-[11px] tracking-[0.3em] uppercase text-[#5C5C5C] hover:text-[#111111] transition-colors duration-300"
                  >
                    <span>
                      {ing.name}
                      <span className="ml-3 text-[#C07C5F] tracking-[0.15em]">{ing.dose}</span>
                    </span>
                    <Plus size={12} className={`text-[#C07C5F] transition-transform duration-500 ${isOpen ? "rotate-45" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p className="pb-5 pr-8 text-sm font-light text-[#5C5C5C] leading-relaxed" data-testid={`ingredient-note-${product.slug}-${slug}`}>
                          {INGREDIENT_NOTES[ing.name]}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>

          {product.nutrition && (
            <div className="mt-10 border border-[#EBE8E3]" data-testid={`nutrition-panel-${product.slug}`}>
              <button
                onClick={() => setShowNutrition(!showNutrition)}
                data-testid={`nutrition-toggle-${product.slug}`}
                className="w-full flex items-center justify-between px-6 py-5 text-[11px] tracking-[0.3em] uppercase hover:bg-white transition-colors duration-500"
              >
                Nutrition Facts
                <Plus size={12} className={`text-[#C07C5F] transition-transform duration-500 ${showNutrition ? "rotate-45" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {showNutrition && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-7">
                      <div className="flex items-baseline justify-between border-b-2 border-[#111111] pb-3 mb-1">
                        <p className="font-serif-lux text-2xl font-medium tracking-tight">Nutrition Facts</p>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C]">{product.nutrition.serving}</p>
                      </div>
                      {(product.nutrition.rows || []).map(toRow).map(({ label, value, indent }) => (
                        <div key={label} className="flex justify-between py-2 border-b border-[#EBE8E3]">
                          <span className={indent ? "pl-4 text-xs font-light text-[#5C5C5C]" : "text-sm font-light"}>{label}</span>
                          <span className={indent ? "text-xs" : "text-sm font-medium"}>{value}</span>
                        </div>
                      ))}
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#C07C5F] mt-6 mb-1">
                        Performance Actives
                      </p>
                      {(product.nutrition.actives || []).map(toActive).map(({ name: label, dose: value }) => (
                        <div key={label} className="flex justify-between py-2 border-b border-[#EBE8E3]">
                          <span className="text-sm font-light">{label}</span>
                          <span className="text-sm font-medium text-[#C07C5F]">{value}</span>
                        </div>
                      ))}
                      <p className="mt-6 text-[11px] font-light text-[#5C5C5C] leading-relaxed">
                        <span className="uppercase tracking-[0.2em] text-[#111111]">Ingredients: </span>
                        {product.nutrition.ingredients}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <div className="mt-10 flex items-center gap-8">
            <span className="font-serif-lux text-4xl font-medium" data-testid={`spotlight-price-${product.slug}`}>
              {formatINR(product.price)}
            </span>
            <button
              onClick={() => add(product.slug)}
              data-testid={`add-to-cart-${product.slug}`}
              className="group flex items-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full px-7 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C07C5F] transition-colors duration-500"
            >
              Add to Cart
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </button>
          </div>
        </motion.div>
      </div>
    </article>
  );
};

export default ProductSpotlight;
